import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import type { User } from '../types/auth';

/**
 * Logged-in Umami tracking for the Steamroom SPA.
 *
 * Separate website id from the Fuzion Trading marketing site. Tracker lives on
 * stats.fznanalytics.com (`/u`, not script.js — EasyList). Heatmaps/replay stay
 * off. Identify with Django user id, never email.
 *
 * Website id and script URL are public (same as fzn-site). Defaults are the
 * live Steamroom App site; VITE_UMAMI_* can override. Do not rely on DO
 * App Platform passing new Dockerfile ARGs — the live spec does not pick
 * up `.do/app.yaml` env keys on git push.
 */

const PUBLIC_PATHS = ['/login', '/reset-password'];
const PRODUCT_DOMAINS = 'app.fzntrading.com,app.steamroomtrading.com';

/** Keep in sync with quantforge `infra/umami/seed-website.sql` (Steamroom App). */
export const UMAMI_WEBSITE_ID = '8022ceb1-a8a9-4c6e-938c-b2b8c652a26e';
export const UMAMI_SCRIPT_SRC = 'https://stats.fznanalytics.com/u';

type UmamiPayload = {
  website?: string;
  hostname?: string;
  language?: string;
  referrer?: string;
  screen?: string;
  title?: string;
  url?: string;
  name?: string;
  data?: Record<string, unknown>;
};

export type UmamiClient = {
  track: {
    (): void;
    (eventName: string, eventData?: Record<string, unknown>): void;
    (update: (props: UmamiPayload) => UmamiPayload): void;
  };
  identify: (
    idOrData: string | Record<string, unknown>,
    data?: Record<string, unknown>,
  ) => void;
};

declare global {
  interface Window {
    umami?: UmamiClient;
  }
}

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

function planOf(user: User): string {
  const tiers = user.stripe_metadata;
  if (!tiers || tiers.length === 0) {
    return 'none';
  }
  return tiers[0] ?? 'none';
}

function pageUrl(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

function ensureUmami(
  websiteId: string,
  scriptSrc: string,
): Promise<UmamiClient | null> {
  if (window.umami) {
    return Promise.resolve(window.umami);
  }

  return new Promise((resolve) => {
    const existing = document.querySelector(
      `script[data-website-id="${websiteId}"]`,
    );
    const finish = (): void => {
      resolve(window.umami ?? null);
    };

    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => resolve(null), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.defer = true;
    script.src = scriptSrc;
    script.setAttribute('data-website-id', websiteId);
    script.setAttribute('data-auto-track', 'false');
    script.setAttribute('data-domains', PRODUCT_DOMAINS);
    script.onload = finish;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
}

export const Analytics: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID || UMAMI_WEBSITE_ID;
  const scriptSrc = import.meta.env.VITE_UMAMI_SCRIPT_SRC || UMAMI_SCRIPT_SRC;
  const [ready, setReady] = useState(false);

  const shouldTrack =
    Boolean(websiteId && scriptSrc) &&
    isAuthenticated &&
    !isLoading &&
    user !== null &&
    !isPublicPath(location.pathname);

  useEffect(() => {
    if (!shouldTrack || !websiteId || !scriptSrc || !user) {
      return;
    }

    let cancelled = false;
    void ensureUmami(websiteId, scriptSrc).then((client) => {
      if (cancelled || !client) {
        return;
      }
      client.identify({
        id: String(user.id),
        plan: planOf(user),
      });
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [shouldTrack, websiteId, scriptSrc, user]);

  useEffect(() => {
    if (!ready || !shouldTrack || !window.umami) {
      return;
    }

    window.umami.track((props) => ({
      ...props,
      url: pageUrl(location.pathname, location.search),
      title: document.title,
    }));
  }, [ready, shouldTrack, location.pathname, location.search]);

  return null;
};

/**
 * Track a named event (no-op until the logged-in tracker has loaded).
 */
export function trackEvent(
  eventName: string,
  eventData?: Record<string, unknown>,
): void {
  window.umami?.track(eventName, eventData);
}
